// The sandbox screen's **coordinator** — the change's only stateful module, and the one
// architecture review **A3** demanded exist (design **T1**/**T2**).
//
// Everything else the screen is built from is a pure function of plain data. This module is not,
// because the screen has genuine concurrency: selecting a version is a compound **activate + load**
// (activate the document server-side, then load that version's trace), a run writes itself over
// seconds while the player keeps editing, and a Beekeeper turn can settle in the middle of either.
// Without one owner, results resolving in an order nobody asked for pair one version's document
// with another version's trace — the exact invariant **D5** promises and the reason this file
// exists.
//
// ## Why a reducer rather than a hook
//
// It is `(state, event) → { state, effects }`. It **describes** side effects and never performs
// them: `activate-version`, `load-trace`, `save-document`, `submit-run`, `await-completion` and
// `poll-run` are data. A thin driver ({@link createSandboxCoordinator}) hands each one to the port
// and dispatches the answer back as another event. That split is what makes every race below a
// table-driven test with **no timers, no DOM and no real async** — and it is the reason the screen
// can be a host that wires things together rather than the next 2000-line component.
//
// The driver **schedules nothing**. There is no interval in this file: the run-poll cadence is the
// screen's decision, arriving as `poll-requested`. A coordinator that owned a timer would put a
// clock back into the one module the tests need to be deterministic.
//
// ## The playhead is state here; the clock that moves it is not
//
// `playback` lives in this state because everything that relocates it — a selection clearing it, a
// trace load parking it on its first tick, a completion autoplaying — is one of the transactions
// above, and a second owner would be free to disagree with them. So the three player-driven moves
// (`seek`, `playback-toggled`, `playhead-advanced`) are arms of this reducer too.
//
// What is *not* here is the loop. `playhead-advanced` arrives in **whole ticks** from the
// composition root, which holds the frame scheduler, the wall clock, the selected speed step and
// the sub-tick remainder between frames (`app/rounds/RoundsController.tsx`, mirroring
// `RoundsReview.tsx`). The split is the same one the run poll makes: cadence is a wall-clock
// decision and belongs to the root; where the playhead lands, and when playback stops, is an
// ordering decision and belongs here.
//
// ## The epoch rule
//
// Every intent that **relocates the working document** — selecting a version, saving, Run (whose
// single request carries the document itself, `rounds-candidate-document` **D8**), and a
// Beekeeper turn settling with a new document — bumps a monotonic
// `epoch`. Effects carry the epoch they were issued under, and any result whose epoch is not
// current is **discarded**, counted in {@link SandboxState.discarded} so a test can prove the stale
// answer arrived and was dropped rather than merely never having shown up.
//
// A *run*, deliberately, is not epoched that way. It outlives selections — the player may look at
// another version while theirs is being written — so progress and completion are matched on
// `runId` plus a monotonic `pollSeq` instead. The epoch re-enters at exactly one point: **autoplay**
// only fires when the selection has not moved since the run was submitted (`run.epoch ===
// state.epoch`). Otherwise the completion is recorded and said out loud, and the world the player
// is actually looking at is left alone — yanking it would be the same crossed pairing wearing a
// different hat.
//
// ### What the epoch cannot do: the server's head (implementation review **I1**)
//
// An epoch discards a stale *response*. It cannot undo a stale *mutation*, and activation is one:
// `POST .../activate` relocates the head. Two selections racing therefore left the head wherever
// the server happened to finish last, so the screen could show v2's document and trace while every
// subsequent save and run operated against v1 — half of what **D5** promises, since the head is
// part of the agreement.
//
// So **activation is serialized**: at most one `activate-version` is outstanding
// (`SandboxState.activating`), and a selection made while one is in flight is held as
// `queuedActivation` and issued when that one settles. Latest intent wins — an intermediate
// selection is superseded and never sent at all, so v3 → v2 → v4 reaches the server as v3 then v4.
// The queue is state and the follow-up is an effect returned from the settle, which keeps the whole
// rule inside the reducer: no timer, no promise, no change to the port.
//
// Unmount is the one case the queue cannot fully close. The reducer's cancellation guard drops the
// queued intent before it can be issued, so no **new** mutation follows an unmount; the activation
// **already** on the wire cannot be recalled, because the client never held it — the server will
// apply it, and the screen that would have cared is gone.
//
// ### A selection supersedes work in flight (implementation review **I2**)
//
// Bumping the epoch under a save or submit stranded it: the response failed the epoch check and was
// discarded, leaving `work` reading `saving`/`submitting` forever — a transport permanently busy
// and, worse, an accepted submission nobody was awaiting, whose score simply never landed.
//
// A selection therefore **supersedes** the work: `work` returns to `idle` with the epoch bump. The
// alternative — refusing the selection for the duration of the POST — buys a total guarantee, but
// it must be paired with withholding the selector in the UI or the control is offered and then says
// no, and it makes the screen refuse a gesture whose only sin is arriving during a request the
// player cannot see. Superseding is chosen because the one thing it must not lose is already the
// rule written above: **a run outlives selections**. So `submit-settled` keeps its accepted run —
// tracked, awaited, stamped with the *submit's* epoch so autoplay stays suppressed and the
// completion is stated rather than yanking the world. A save superseded this way loses only an
// uncommitted commit the player replaced by choosing another version, and a superseded `run` intent
// never reaches the submit at all, so nothing on the server is orphaned by it.
//
// ## Two deliberate non-behaviours
//
//   * **There is no navigation effect and no toast effect**, and there is no state field a modal
//     could be driven from. The run-completion toast is deleted (**D6**) because it existed to
//     navigate to a screen that no longer exists; a completed run simply seeks to its first tick
//     and plays.
//   * **Committing a version does not blank the world.** The loaded world names its own `runId`,
//     and the version selector states separately that the new version has not been run — so nothing
//     disagrees. Blanking on Save would destroy the very frame the player is diagnosing from while
//     they type, which is the loop this screen exists to tighten.
//
// The port is a **narrow interface the screen supplies**, not the HTTP client: six methods, no
// run listing. The coordinator sequences async work; it does not own the screen's reads.
//
// ### The ordinal Run's single request now carries (implementation review HIGH, post-D8)
//
// Design `rounds-candidate-document` **D8** collapses Run to one request. The accept response
// used to be `{runId}` alone — no ordinal, because it was never extended to — so the instant a
// dirty Run was accepted, the coordinator knew the *bytes* the server now held (they are exactly
// what it just sent) but not the *ordinal* the server allocated them: cutting a version, the way
// `saveDocument` used to report back explicitly, was folded into the server's commit and said
// nowhere on that response.
//
// The fix this module now carries is on the **response**, not a follow-up read: the accept
// response names `documentVersion`, the exact ordinal `commit_candidate` cut inside its own
// critical section (`CommittedCandidate::document_version`), so `submit-settled` applies it
// directly to `document.version` with no second round trip. This module used to close the gap
// with a `confirm-head-ordinal` **read** of the sandbox's mutable head, issued alongside
// `await-completion`. That was unsound in a way guessing `baseOrdinal + 1` would also have been,
// but worse: the head is a **shared, mutable** value, so a second writer — another tab, another
// device, a retry — committing between this run's own commit and that read landing meant the
// read could return *the other writer's* ordinal, which the coordinator would then record as
// this run's own. The next dirty press built its `baseOrdinal` from exactly that number
// (`RoundsController.tsx`'s `headVersion`), so the corrupted attribution became live, valid-
// looking input to the very CAS design **D7** relies on to prevent this class of defect — the
// client would go on to submit its own (now-stale) bytes stamped with the *other* writer's
// version, and a CAS that only compares ordinals cannot tell that apart from a legitimate
// edit-and-resubmit. The read passed, and the other writer's commit was silently overwritten.
// Carrying the value the commit itself returns makes that race unreachable by construction:
// there is no longer anything to poll, so there is nothing for a second writer to win a race
// against.

import type {
  ActivateDispatch,
  HandEditDispatch,
  HandEditResult,
  RunStatus,
} from '../../net/rounds-http';
import type { ReviewTrace, TraceLoad } from '../../net/rounds-review-source';
import type { DocumentDiagnostic, RejectReason } from '../../net/wire/messages';
import type {
  RunCandidate,
  RunDiagnostic,
  RunOutcome,
  RunRefusal,
  RunSubmission,
} from '../colony/run-controller';
import type { TransportInput } from './run-submit-model';
import {
  decideSeekToTick,
  seekableWindow,
  type RunExecutionSite,
  type WrittenExtent,
} from './written-extent';

// ---------------------------------------------------------------------------
// The port
// ---------------------------------------------------------------------------

/**
 * `ActivateDispatch` now comes from `net/rounds-http.ts`, which declares it alongside the
 * `activateVersion` method — the re-home this module's placeholder was written to anticipate.
 *
 * Its `applied` arm nests the body under `result`, exactly as `HandEditDispatch` does, rather than
 * spreading it flat: one shape for all three routes that move the document means one store-apply
 * path. The placeholder here guessed flat; the nested form won because consistency with the
 * shipped `HandEditDispatch` matters more than either guess.
 *
 * Importing it is `import type` only, like every other import in this module's header — the
 * reducer still depends on no concrete client, which is what keeps it testable against the fake.
 */

/**
 * The fields of a run the coordinator reads while polling. Deliberately a subset of `RunDto` +
 * the contract's `documentVersion`: a wider type would let this module start deciding things
 * (scores, evidence, provenance) that belong to `version-menu-model.ts` and the counters.
 */
export interface RunProgressDto {
  readonly runId: number;
  readonly status: RunStatus;
  readonly ticksObserved: number;
  readonly terminalTick: number | null;
}

/**
 * The asynchronous operations the coordinator needs, and nothing else.
 *
 * **This is the dependency — not `RoundsHttpClient`.** Inverting it here is what keeps the reducer
 * pure and lets the screen (and the tests' fake sandbox) wire whichever transport it holds. Seven
 * methods is the whole surface; anything wider is a read the screen should be doing itself.
 */
export interface SandboxPort {
  /** `POST /rounds/{sandbox}/versions/{ordinal}/activate` — relocates the head and returns the
   * whole document. */
  activateVersion(ordinal: number): Promise<ActivateDispatch>;
  /** The whole-document hand-edit save. `baseVersion` is the ordinal the edit was made against,
   * `null` when the editor has never held a committed version. */
  saveDocument(source: string, baseVersion: number | null): Promise<HandEditDispatch>;
  /** Submits the whole-colony program set as one candidate. */
  submitRun(candidate: RunCandidate): Promise<RunSubmission>;
  /** The **one-shot** completion signal: a long-lived GET that resolves exactly once per run. It
   * is not a poll and must not be re-issued for a run already awaited. */
  awaitCompletion(runId: number): Promise<RunOutcome>;
  /** One progress read. The cadence belongs to the caller; this answers a single question. */
  fetchRun(runId: number): Promise<RunProgressDto | null>;
  /** Opens a published run's trace. */
  openTrace(runId: number): Promise<TraceLoad>;
  /**
   * Opens the tournament's shared **preview** trace (`rounds-preview-run`). `TraceOutcome`
   * rather than `TraceLoad`, matching `load-trace`'s own effect: a transport failure and a
   * not-yet-published preview both settle here as something other than `loaded`, and the
   * reducer's `preview-settled` handling treats every non-`loaded` outcome alike — nothing here
   * distinguishes "pending" from "failed" (`client-sandbox-screen`'s empty state renders the
   * same either way). Absent a tournament (practice), the screen never issues the intent this
   * responds to, so a practice port's implementation is never called.
   */
  openPreview(): Promise<TraceOutcome>;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** The working document as the server last handed it back. */
export interface DocumentSnapshot {
  readonly version: number;
  readonly source: string;
  /**
   * Whether the document compiles server-side. **Absent, not `false`, when the surface performs
   * no compile at all** — a practice sandbox's hand-edit and activate routes report nothing here
   * because the compile happens on the device, and that is "unknown", never "does not parse".
   * `transportInputFor` and `SandboxScreen.tsx` both read this through `?? true`, which is what
   * makes an absent value the same as "assume it compiles" rather than a fabricated failure.
   */
  readonly valid?: boolean;
  readonly diagnostics: readonly DocumentDiagnostic[];
}

/** Uncommitted editor text. Its presence *is* the dirty flag — there is no separate boolean to
 * drift from it. */
export interface DraftState {
  readonly source: string;
  /**
   * The client's pre-echo of the server's `document_invalid` refusal (design **T6**), never a
   * second source of truth.
   *
   * **Absent, not `false`, when no verdict has been reached for these bytes yet** — the same
   * unknown/absent distinction {@link DocumentSnapshot.valid} draws, and for a related reason. In a
   * practice sandbox nothing server-side compiles the document: the **device** does
   * (`rounds-local-run`, task 5.12), and a device compile is asynchronous while an edit is not. So
   * between the keystroke and the verdict the honest answer is "not known", and every reader takes it
   * through `?? true` — which keeps the pending window *permissive* rather than blocking Run behind a
   * fabricated failure. Nothing is lost by that: the real gate is the on-device compile the run
   * controller performs before the run-start request, which no unknown draft can bypass.
   */
  readonly compiles?: boolean;
  readonly diagnostics: readonly DocumentDiagnostic[];
}

/**
 * The selection **transaction's** state — not the version menu's highlight, which
 * `version-menu-model.ts` derives from the head ordinal. This says which activate+load pair is in
 * flight and which one last settled, so a result can be matched to the intent that asked for it.
 */
export type SandboxSelection =
  | { readonly kind: 'none' }
  | {
      readonly kind: 'pending';
      readonly epoch: number;
      readonly ordinal: number;
      readonly runId: number | null;
    }
  | {
      readonly kind: 'settled';
      readonly epoch: number;
      readonly ordinal: number;
      readonly runId: number | null;
    };

/** What the world view has to draw. `empty` carries **why**, because "no run" and "the run would
 * not load" are different sentences and the screen must say the right one. */
export type SandboxWorld =
  | { readonly kind: 'empty'; readonly reason: 'no-selection' | 'not-run' | 'load-failed' }
  /**
   * A run's verified trace is being read, and — when the run is one this client was already
   * **watching** — the unverified prefix it is superseding is still held here.
   *
   * ## Why `watching` is on this arm rather than dropped
   *
   * Publishing is a ~130 ms read, and this arm used to carry nothing through it. That cost the
   * player two things at once. The world had no trace, so `SandboxScreen` gated the review frame off
   * and **unmounted the canvas** mid-run, resetting its camera (measured: 4.00x -> 1.31x). And by the
   * time the verified trace arrived there was no longer any evidence that the playhead was already
   * positioned *inside this same run*, so the load could only reset it (measured: tick 658 -> 1).
   * Both read as "the simulation restarted randomly", because from the player's seat that is
   * indistinguishable from what happened.
   *
   * So the prefix is kept, and it does double duty: it is what the world keeps drawing across the
   * gap, and it is the fact `trace-settled` consults to tell "the same run, read a second and better
   * way" from "a different run, which must open at its own beginning".
   *
   * `null` whenever there was no watched prefix to carry — a selection of another version, or any
   * server-executed run, where the same bytes sit across a process boundary and reading them early
   * would bypass the commit marker (**D-121**). The field is named `watching`, not `trace`, for the
   * same reason the `in-flight` arm's is: nothing holding this may believe it read a published,
   * checksum-verified trace.
   */
  | {
      readonly kind: 'loading';
      readonly runId: number;
      readonly watching: ReviewTrace | null;
    }
  /**
   * **A run being watched while it is still being written** (task 5.13; `rounds-trace`'s in-process
   * carve-out, design **D-D**; `client-practice` "Watching begins before the run completes").
   *
   * Reachable only where this client owns the writer — a device-executed practice run — and the
   * reducer refuses it outright for `runExecution: 'server'`, where the same read would cross the
   * commit marker (**D-121**).
   *
   * ## Why this is its own arm and not a `loaded` with a shorter trace
   *
   * The rule is `openInFlight`'s, mirrored rather than reinvented: "nothing that believes it read a
   * published, checksum-verified trace can be holding an unverified prefix instead". `LocalTraceOpen`
   * and `InFlightTraceOpen` are two unions with no assignability in either direction for exactly this
   * reason, and `DecodedTrace`/`InFlightPrefix` carry opposite `verification` brands one layer down.
   * `ReviewTrace` is the screen's seam and carries no brand of its own, so the distinction has to live
   * in the arm — which is also what makes it impossible for a consumer to *accidentally* accept one:
   * the field is named {@link watching} rather than `trace`, so `world.trace` does not compile here
   * and every reader has to say which of the two it means.
   */
  | {
      readonly kind: 'in-flight';
      readonly runId: number;
      /** Stated rather than implied, so a test — and a reader — can see what this arm holds without
       * following it back through three files. */
      readonly verification: 'in-process-unverified';
      /**
       * The extent written so far, adapted into the same seam the world view already draws. Its
       * `terminalTick` is the run's configured **horizon**, not the write head: the track depicts the
       * whole run, which is what makes the written extent distinguishable from the remainder.
       *
       * **This is the one `ReviewTrace` in the system that is not immutable, and a consumer must not
       * cache it.** `createPracticeWatcher` owns exactly one trace per run and *grows it in place*,
       * because minting a new one per delta forced a full renderer sync per delta — the measured cost
       * this arm's whole shape exists to avoid. The reducer never mutates it and stays pure; it stores
       * a reference to a value the watcher keeps writing to.
       *
       * So: read it through {@link watchableTrace} at the point of use, and if you need to know whether
       * it grew, compare {@link ReviewTrace.extentVersion} — never the reference, which is stable by
       * design, and never a snapshot of a field taken earlier, which is live. Today's only consumers
       * are `watchableTrace`, this module's own arms, and `SandboxScreen`'s per-render read; a consumer
       * that wanted a stable copy would have to take one deliberately.
       */
      readonly watching: ReviewTrace;
    }
  | {
      readonly kind: 'loaded';
      readonly runId: number;
      readonly trace: ReviewTrace;
      /**
       * Whether this is the tournament's shared **preview** trace rather than one of the
       * player's own runs (`rounds-preview-run`). `runId` is {@link PREVIEW_RUN_ID}, a sentinel
       * that never collides with a server-assigned id.
       *
       * Optional so every existing construction of this arm (a real run's own trace) stays a
       * valid value with the field simply absent — nothing here narrows on it except the one
       * screen-level check that must not count the preview as a candidate.
       */
      readonly isPreview?: boolean;
    };

/**
 * Mutating work in flight.
 *
 * `saving` is the whole-document hand-edit's own state (`hand-edit-requested`) — a save Run no
 * longer performs. Design `rounds-candidate-document` **D8** folds Run's commit into the single
 * `submit-run` request the server commits atomically, so there is no longer a save that "continues
 * into a submit"; `run-requested` goes straight to `submitting`.
 */
export type SandboxWork =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving'; readonly epoch: number }
  | { readonly kind: 'submitting'; readonly epoch: number };

/** The run this screen is following. */
export type SandboxRun =
  | { readonly kind: 'none' }
  | {
      readonly kind: 'in-flight';
      readonly runId: number;
      /** The document epoch at submit time — consulted only to decide autoplay. */
      readonly epoch: number;
      readonly ticksObserved: number;
      /** `null` until the run reports one; never `0`, which would read as a stalled run. */
      readonly expectedTerminalTick: number | null;
      /** The newest poll issued. */
      readonly issuedPollSeq: number;
      /** The newest poll whose answer was applied — the stale-response gate. */
      readonly appliedPollSeq: number;
    }
  | { readonly kind: 'settled'; readonly runId: number; readonly outcome: RunOutcome };

export interface PlaybackState {
  readonly playheadTick: number | null;
  readonly playing: boolean;
}

/** Something to say to the player, rendered inline. **Never a modal** — nothing here interrupts
 * the editor. */
export interface SandboxNotice {
  readonly tone: 'info' | 'error';
  readonly message: string;
  /** The compiler's own diagnostics behind a `compileFailed` refusal, so the blocked-Run popup
   * shows the server's words rather than the client's guess. Empty for every other notice. */
  readonly diagnostics: readonly RunDiagnostic[];
}

export interface SandboxState {
  /** Monotonic. Bumped by every intent that relocates the working document. */
  readonly epoch: number;
  /** Results arriving after unmount are dropped rather than applied. */
  readonly cancelled: boolean;
  /** How many results have been discarded as stale or cancelled — an assertable count, so a test
   * can prove a stale answer *arrived* and was dropped. */
  readonly discarded: number;
  readonly selection: SandboxSelection;
  /**
   * An `activate-version` effect has been issued and has not yet settled.
   *
   * Distinct from `selection.kind === 'pending'`, which spans the whole activate+load transaction:
   * this names only the **mutating** half. A trace load is a read and races harmlessly; the head is
   * a server-side write and may not (**I1**).
   */
  readonly activating: boolean;
  /**
   * The newest ordinal selected while an activation was outstanding, issued when that one settles.
   * A single slot rather than a queue: the player wants the version they asked for **last**, and
   * replaying the ones in between would move the head through documents nobody is looking at.
   */
  readonly queuedActivation: number | null;
  readonly document: DocumentSnapshot | null;
  readonly draft: DraftState | null;
  readonly world: SandboxWorld;
  readonly work: SandboxWork;
  readonly run: SandboxRun;
  readonly playback: PlaybackState;
  readonly notice: SandboxNotice | null;
  /** A Beekeeper turn holds the sandbox: the server refuses Activate, Run and hand edits, so this
   * screen withholds the same three ({@link withheldSurfaces}). */
  readonly turnInFlight: boolean;
  /**
   * **Where this sandbox's runs execute** — and therefore whether an in-flight run's written extent
   * is seekable (`client-sandbox-screen`, design **D-D**; `written-extent.ts`'s header carries the
   * reasoning).
   *
   * State rather than a `writtenExtentFor` parameter, because the *reducer* adjudicates seeks too:
   * a second source would let the track's affordance and the coordinator's re-check disagree, and
   * the disagreement that matters is the permissive one. It is a property of the whole coordinator
   * rather than of any one run — a practice sandbox's runs all execute on the device, a
   * tournament's all on the server — so it is set once at construction and never transitions.
   *
   * `'server'` in {@link initialSandboxState}, deliberately: a caller that forgets to say lands on
   * the conservative side of **D-121**.
   */
  readonly runExecution: RunExecutionSite;
  /** An `open-preview` effect is in flight (`rounds-preview-run`). Guards against issuing a
   * second concurrent read while the first has not yet settled — `preview-requested` is
   * idempotent while this is `true`. */
  readonly previewRequested: boolean;
}

/** The sentinel `runId` a preview-loaded world carries. Never a value the server assigns to a
 * real run (`rounds-preview-run`: "its run id is never accepted or disclosed"). */
export const PREVIEW_RUN_ID = -1;

/** A fresh screen: nothing selected, nothing loaded, nothing in flight. */
export function initialSandboxState(): SandboxState {
  return {
    epoch: 0,
    cancelled: false,
    discarded: 0,
    selection: { kind: 'none' },
    activating: false,
    queuedActivation: null,
    document: null,
    draft: null,
    world: { kind: 'empty', reason: 'no-selection' },
    work: { kind: 'idle' },
    run: { kind: 'none' },
    playback: { playheadTick: null, playing: false },
    notice: null,
    turnInFlight: false,
    runExecution: 'server',
    previewRequested: false,
  };
}

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

/** Why a trace is being opened. It decides autoplay, and nothing else. */
export type TraceLoadReason = 'selection' | 'completion';

/**
 * Every effect the reducer can describe.
 *
 * Note the two that are **absent**: there is no navigation effect and no toast/modal effect. The
 * completion toast is deleted (**D6**), and a screen that cannot describe a navigation cannot
 * perform one — which is a stronger guarantee than a component that merely does not call one today.
 */
export type SandboxEffect =
  | { readonly kind: 'activate-version'; readonly epoch: number; readonly ordinal: number }
  | {
      readonly kind: 'load-trace';
      readonly epoch: number;
      readonly runId: number;
      readonly reason: TraceLoadReason;
    }
  | {
      readonly kind: 'save-document';
      readonly epoch: number;
      readonly source: string;
      readonly baseVersion: number | null;
    }
  | { readonly kind: 'submit-run'; readonly epoch: number; readonly candidate: RunCandidate }
  | { readonly kind: 'await-completion'; readonly runId: number }
  | { readonly kind: 'poll-run'; readonly runId: number; readonly pollSeq: number }
  /** Reads the tournament's shared preview trace (`rounds-preview-run`). Carries nothing: the
   * preview is addressed by no run id, no ordinal, no epoch — it names no candidate and belongs
   * to no in-flight selection or run. */
  | { readonly kind: 'open-preview' };

/** The effect kinds, as data — so a test can assert both that every emitted effect is one of these
 * and that the list contains no navigation or modal kind. */
export const SANDBOX_EFFECT_KINDS = [
  'activate-version',
  'load-trace',
  'save-document',
  'submit-run',
  'await-completion',
  'poll-run',
  'open-preview',
] as const;

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** A trace open's outcome, widened by the one case `TraceLoad` cannot express: the request never
 * answered at all. */
export type TraceOutcome = TraceLoad | { readonly kind: 'unavailable'; readonly message: string };

export type SandboxEvent =
  // --- player intents ---
  /** A version row was selected. `runId` is the candidate the row stands for, `null` when the
   * version has never been run. */
  | { readonly kind: 'select-version'; readonly ordinal: number; readonly runId: number | null }
  /** The editor's text changed. */
  | {
      readonly kind: 'document-edited';
      readonly source: string;
      /** Absent when no verdict has been reached for these bytes yet — see
       * {@link DraftState.compiles}. A surface whose compile is asynchronous omits it and follows with
       * a {@link SandboxCoordinatorEvent} of kind `draft-compiled`. */
      readonly compiles?: boolean;
      readonly diagnostics: readonly DocumentDiagnostic[];
    }
  /**
   * **A verdict landing for a draft already in hand** (task 5.12).
   *
   * Separate from `document-edited` rather than a second one of them, because re-editing would reset
   * the draft: the draft's *bytes* are the edit's, and this only attaches the verdict those bytes
   * earned. `source` is carried so a verdict for superseded text is **discarded** rather than applied
   * to whatever is in the editor now — with an asynchronous compiler, two edits in flight can land out
   * of order, and the stale one must not win.
   */
  | {
      readonly kind: 'draft-compiled';
      readonly source: string;
      readonly compiles: boolean;
      readonly diagnostics: readonly DocumentDiagnostic[];
    }
  /** Save, without running. */
  | { readonly kind: 'hand-edit-requested' }
  /** Run — one request carrying the working document and its base ordinal; the server commits
   * an uncommitted document as part of the same request (`rounds-candidate-document` **D8**). */
  | { readonly kind: 'run-requested'; readonly candidate: RunCandidate }
  /** The screen's poll tick. The cadence is the screen's; this file owns only the ordering. */
  | { readonly kind: 'poll-requested' }
  /**
   * A drag on the scrub track resolved to a tick. **Adjudicated against the written extent**, not
   * honoured blindly: a seek into a run still being written is refused (`written-extent.ts`), and a
   * published one is clamped to its trace's own ends.
   */
  | { readonly kind: 'seek'; readonly tick: number }
  /** The transport was pressed in its `playing`/`paused` state. */
  | { readonly kind: 'playback-toggled' }
  /**
   * The composition root's frame tick, in **whole** ticks. The loop that produces it holds the
   * wall clock, the speed step and the sub-tick remainder (design **T2**); this module owns only
   * where the playhead ends up and when playback stops.
   */
  | { readonly kind: 'playhead-advanced'; readonly ticks: number }
  /** Unmount. */
  | { readonly kind: 'cancelled' }
  // --- results ---
  | {
      readonly kind: 'activation-settled';
      readonly epoch: number;
      readonly ordinal: number;
      readonly dispatch: ActivateDispatch;
    }
  | {
      readonly kind: 'trace-settled';
      readonly epoch: number;
      readonly runId: number;
      readonly reason: TraceLoadReason;
      readonly outcome: TraceOutcome;
    }
  | { readonly kind: 'save-settled'; readonly epoch: number; readonly dispatch: HandEditDispatch }
  | {
      readonly kind: 'submit-settled';
      readonly epoch: number;
      readonly submission: RunSubmission;
    }
  | {
      readonly kind: 'run-progress';
      readonly runId: number;
      readonly pollSeq: number;
      /** `null` when the read failed or the run has gone — information the reducer declines to
       * invent a value for. */
      readonly dto: RunProgressDto | null;
    }
  /**
   * **The device reported the extent it has already written**, decoded and ready to draw (task 5.13).
   *
   * Pushed by the run's own worker, never polled: the count the track grows from is `run-progress`'s
   * and arrives through `fetchRun`, while this is world state and arrives through the run seam's
   * watch channel. Two signals with two owners, which is why they are two events.
   *
   * Carries a `ReviewTrace` rather than a prefix so this module stays ignorant of the trace format —
   * the adaptation is `net/rounds-review-source.ts`'s `reviewTraceFromInFlight`, accumulated per run
   * by `app/rounds/practice-watch.ts`.
   */
  | { readonly kind: 'run-watched'; readonly runId: number; readonly watching: ReviewTrace }
  | { readonly kind: 'run-completed'; readonly outcome: RunOutcome }
  /**
   * The screen's intent to try loading the tournament's shared preview trace
   * (`rounds-preview-run`). Mirrors `poll-requested`: the *cadence* (when it is worth trying) is
   * the screen's, over conditions it already reads (no candidate yet, nothing else in the
   * world), while *whether it is eligible right now* and *what to do with the answer* are the
   * reducer's. Idempotent while an attempt is already in flight or the world is already claimed.
   */
  | { readonly kind: 'preview-requested' }
  /** The `open-preview` effect settled. Every non-`loaded` outcome (pending, failed, corrupt,
   * unsupported, unavailable) is treated alike — rendered as nothing, per
   * `client-sandbox-screen`'s "ordinary nothing-has-been-run-yet empty state, with no error
   * chrome and no mention of a preview". */
  | { readonly kind: 'preview-settled'; readonly outcome: TraceOutcome }
  // --- the Beekeeper's turn ---
  | { readonly kind: 'turn-started' }
  /** A turn settled. `document` is the version it cut, or `null` when it produced none. */
  | { readonly kind: 'turn-settled'; readonly document: DocumentSnapshot | null };

export interface SandboxTransition {
  readonly state: SandboxState;
  readonly effects: readonly SandboxEffect[];
}

// ---------------------------------------------------------------------------
// Messages
//
// Every sentence the coordinator can produce lives here, exported, so a test binds to the message
// rather than restating its prose — and so no component invents a second wording for the same
// condition. All of them are facts about the sandbox, never apologies.
// ---------------------------------------------------------------------------

/** The three surfaces a turn withholds say one thing, because it is one condition. */
export const TURN_WITHHELD_MESSAGE =
  'The Beekeeper is rewriting this colony. Editing, running and version changes come back when the turn ends.';

/** A hand edit that was saving when a turn replaced the whole document. The edit did not land —
 * but the text is still in the editor, so nothing typed was lost. */
export const HAND_EDIT_SUPERSEDED_MESSAGE =
  'The Beekeeper finished a turn while your save was in flight, so your edit was not committed. Your text is still in the editor — press Save again to keep it.';

/** Activation succeeded, the trace did not (**4.2**). Says all three things: which version is now
 * in the editor, that the run would not load, and that the empty world is deliberate. */
export function traceLoadFailedMessage(ordinal: number | null, detail: string): string {
  const subject =
    ordinal === null
      ? 'That run could not be loaded'
      : `Version ${ordinal} is now in the editor, but its run could not be loaded`;
  return `${subject} (${detail}). The world view is empty rather than showing the previous run.`;
}

/**
 * A retained run recorded before a trace-format bump (`bee-state-flash` task 4.6).
 *
 * Deliberately **not** {@link traceLoadFailedMessage}: a damaged trace means something went wrong
 * and is worth reporting, while a stale one means only that the app was updated since the run was
 * written — the ordinary consequence of shipping a format bump. Telling a player their run is
 * corrupted when the app simply moved on invites them to chase a fault that does not exist. So this
 * says what actually happened, and what to do about it: run it again.
 */
export function traceStaleFormatMessage(ordinal: number | null): string {
  const subject =
    ordinal === null
      ? 'That run was recorded'
      : `Version ${ordinal} is now in the editor, but its run was recorded`;
  return `${subject} by an older version of Waggle, so this build can no longer replay it. Nothing is damaged — run it again to watch it.`;
}

/** The whole selection failed before the document moved. */
export function activationFailedMessage(ordinal: number, detail: string): string {
  return `Version ${ordinal} could not be activated (${detail}). The editor is still on the document it had.`;
}

/** A typed submit rejection, said in the player's terms (**4.6**). */
export function runRefusalMessage(refusal: RunRefusal): string {
  switch (refusal.kind) {
    case 'compileFailed': {
      const count = refusal.diagnostics.length;
      return `This colony does not compile, so no run was started — ${count} ${count === 1 ? 'error' : 'errors'} in the program set.`;
    }
    case 'admission': {
      const subject =
        refusal.scope === 'player'
          ? 'You already have a run in flight'
          : 'The batch service is at its limit';
      return `${subject} — ${refusal.inFlight} of ${refusal.limit} ${refusal.limit === 1 ? 'slot' : 'slots'} in use. Try again in a moment.`;
    }
    case 'unauthorized':
      return 'This session does not own this sandbox, so the run was refused.';
    case 'stale':
      return 'This document moved on since you last edited it, so the run was refused. Reopen the current version and try again.';
    case 'turnInProgress':
      // The third of the three surfaces a turn withholds, and it says the same sentence as the
      // other two deliberately: it is one condition, and a player who has just been told this on
      // the editor should not read a differently-worded version of it on Run.
      return TURN_WITHHELD_MESSAGE;
    case 'invalid':
      return `The run was refused: ${refusal.error}.`;
    case 'unavailable':
      return `The run could not be sent: ${refusal.message}.`;
  }
}

/** A hand-edit save refusal. */
export function saveRefusalMessage(reason: RejectReason): string {
  switch (reason.kind) {
    case 'turnInProgress':
      return TURN_WITHHELD_MESSAGE;
    case 'compileFailed': {
      const count = reason.diagnostics.length;
      return `The save was refused: the document does not compile — ${count} ${count === 1 ? 'error' : 'errors'}.`;
    }
    case 'stale':
      return 'This document moved on while you were editing, so the save was refused. Re-open the version and apply your change again.';
    case 'unauthorized':
      return 'This session does not own this sandbox, so the save was refused.';
    case 'rateLimited':
      return 'Saves are coming in faster than the surface accepts. Try again in a moment.';
    case 'upgrade':
      return `This client is too old for the server (it wants version ${reason.minVersion} or newer). Reload to update.`;
    case 'invalid':
      return 'The save was refused as invalid.';
  }
}

/** A run that ended with no score. Never fabricates one. */
export function runFailureMessage(runId: number, status: string, detail?: string): string {
  const because = detail === undefined ? status : `${status}: ${detail}`;
  return `Run ${runId} ended without a score (${because}).`;
}

/** A run that finished while the player was looking at a different version. Stated rather than
 * acted on — the world they are watching is not taken away from them. */
export function runCompletedElsewhereMessage(runId: number, score: number): string {
  return `Run ${runId} finished with a score of ${score}. Select it in the version list to watch it.`;
}

/** Run pressed on a draft the client already knows will not compile. The transport renders
 * `blocked` and raises the diagnostics instead of dispatching this, so reaching it is defensive —
 * but a silent no-op would be the one outcome the spec forbids. */
export function documentBlockedMessage(errorCount: number): string {
  return `This draft has ${errorCount} ${errorCount === 1 ? 'error' : 'errors'}, so there is nothing to run yet.`;
}

// ---------------------------------------------------------------------------
// Derivations — the pure models, composed
// ---------------------------------------------------------------------------

/** The three surfaces a Beekeeper turn withholds, per the spec's "matching the server's refusal of
 * the same three". `true` means withheld. */
export function withheldSurfaces(state: SandboxState): {
  transport: boolean;
  versionSelector: boolean;
  handEdit: boolean;
} {
  return {
    transport: state.turnInFlight,
    versionSelector: state.turnInFlight,
    handEdit: state.turnInFlight,
  };
}

/** The coordinator's state → `run-submit-model.ts`'s input. Kept here so the screen never
 * assembles those six booleans by hand and drifts. */
export function transportInputFor(state: SandboxState): TransportInput {
  return {
    turnInFlight: state.turnInFlight,
    // A submit still in flight is as un-re-submittable as a run already accepted.
    runInFlight: state.run.kind === 'in-flight' || state.work.kind !== 'idle',
    // `?? true` through both levels, so an unverdicted draft is permissive rather than blocking — see
    // `DraftState.compiles`. The gate that actually refuses a non-compiling document is the compile the
    // run controller performs before its request, on either path.
    documentCompiles: state.draft?.compiles ?? state.document?.valid ?? true,
    documentDirty: state.draft !== null,
    // **`loaded` only, and a run in flight outranks this anyway.** A watched extent is not something
    // the transport control offers Play over: `transportStateFor` returns `busy` while
    // `runInFlight` is true, so the control says "this run is being written" and playback of the
    // written extent is the playhead loop's, not a button's. Widening this to the in-flight arm
    // would change nothing today and would silently offer Play the moment the precedence changed.
    //
    // **The preview is excluded, and the field's own name is why** (E2E finding M-6): this asks
    // whether *the selected version's* run is playable, and the tournament's shared preview belongs
    // to no selection and is not one of the player's runs (`rounds-preview-run`). Counting it made
    // the single transport control read `paused` from the moment the preview auto-loaded, so Run
    // was unreachable for the whole editing window — including after a Beekeeper turn cut a version,
    // since `turn-settled` leaves the preview sitting in the world slot.
    selectedRunPlayable: state.world.kind === 'loaded' && state.world.isPreview !== true,
    playing: state.playback.playing,
  };
}

/**
 * **The trace the world view draws**, whichever arm holds it — the one place the two are unified.
 *
 * Exported because three consumers need the same answer and a fourth spelling of it is how the screen
 * and the playhead loop end up drawing different runs: `SandboxScreen`'s world frame, the composition
 * root's `usePlayheadLoop`, and this module's own playhead arms.
 *
 * It unifies *drawing* and nothing else. What may be **seeked** into is still `written-extent.ts`'s,
 * split by execution site, and how far the playhead may advance is {@link watchableUpperTick}'s —
 * because a watched trace's `terminalTick` is the run's horizon, which is far ahead of what has
 * actually been written.
 */
export function watchableTrace(state: SandboxState): ReviewTrace | null {
  switch (state.world.kind) {
    case 'loaded':
      return state.world.trace;
    case 'in-flight':
      return state.world.watching;
    case 'loading':
      // A run whose own watched prefix is being superseded keeps drawing that prefix until the
      // verified trace lands — otherwise the canvas unmounts for the length of the read. `null` for
      // a load that has nothing behind it, which is still every server-executed run and every
      // selection of another version.
      return state.world.watching;
    default:
      return null;
  }
}

/**
 * The highest tick the playhead may reach, or `null` when there is nothing to play.
 *
 * For a published trace that is its terminal tick. For a **watched** one it is the write head, taken
 * from the same {@link seekableWindow} the scrub track's affordance and the seek gesture read — so a
 * playhead cannot advance to a moment a drag is refused at, which would be the two halves of one rule
 * disagreeing.
 */
export function watchableUpperTick(state: SandboxState): number | null {
  if (state.world.kind === 'loaded') {
    return state.world.trace.terminalTick;
  }
  if (state.world.kind !== 'in-flight') {
    return null;
  }
  return seekableWindow(writtenExtentFor(state))?.to ?? null;
}

/** The coordinator's state → `written-extent.ts`'s input. A run being written outranks a loaded
 * world: the track must show the *new* run's progress, not the old one's full bar. */
export function writtenExtentFor(state: SandboxState): WrittenExtent {
  if (state.run.kind === 'in-flight') {
    return {
      kind: 'in-flight',
      // Carried from the coordinator's own configuration, so the screen cannot supply one that
      // disagrees with the one the reducer adjudicates against.
      site: state.runExecution,
      ticksObserved: state.run.ticksObserved,
      expectedTerminalTick: state.run.expectedTerminalTick,
      // **Only from a watched world that belongs to THIS run.** The two arms carry independent run
      // ids — a player who selects another version while a run writes leaves a world for one run
      // beside a run in flight for another — and a head taken from the wrong one would bound this
      // run's playhead by a different run's extent. Where they do not match there is no watched
      // prefix for the active run at all, so the field is omitted and `seekableWindow` falls back to
      // the pre-existing `ticksObserved` behaviour rather than this function inventing a bound.
      ...(state.world.kind === 'in-flight' && state.world.runId === state.run.runId
        ? { watchedHeadTick: state.world.watching.writeHeadTick ?? null }
        : {}),
    };
  }
  if (state.world.kind === 'loaded') {
    return {
      kind: 'published',
      firstTick: state.world.trace.firstTick,
      terminalTick: state.world.trace.terminalTick,
    };
  }
  return { kind: 'absent' };
}

// ---------------------------------------------------------------------------
// The reducer
// ---------------------------------------------------------------------------

const NO_EFFECTS: readonly SandboxEffect[] = [];

function settle(state: SandboxState, effects: readonly SandboxEffect[] = NO_EFFECTS): SandboxTransition {
  return { state, effects };
}

/** A result that belongs to an intent the screen has left behind, or to a screen that is gone. */
function discard(state: SandboxState): SandboxTransition {
  return settle({ ...state, discarded: state.discarded + 1 });
}

function error(message: string, diagnostics: readonly RunDiagnostic[] = []): SandboxNotice {
  return { tone: 'error', message, diagnostics };
}

function info(message: string): SandboxNotice {
  return { tone: 'info', message, diagnostics: [] };
}

/** The current selection's ordinal, for a message that wants to name it. */
function selectedOrdinal(selection: SandboxSelection): number | null {
  return selection.kind === 'none' ? null : selection.ordinal;
}

/** Why a trace load **failed**, in the transport's own words.
 *
 * `unsupported` is excluded by the type rather than handled here: a stale-format trace did not fail,
 * so it has no failure detail to report and gets {@link traceStaleFormatMessage} instead. Excluding
 * it structurally is what stops a future arm from quietly falling through to corruption prose. */
function traceFailureDetail(
  outcome: Exclude<TraceOutcome, { kind: 'loaded' } | { kind: 'unsupported' }>,
): string {
  return outcome.kind === 'corrupt' ? `${outcome.component}: ${outcome.message}` : outcome.message;
}

/**
 * A tick brought inside a trace's own span, as a whole tick.
 *
 * The playhead is an **integer** everywhere it is read — `detailAt`, the mm:ss clock, the chip
 * counts — so the fractional part of a frame's advance is the frame loop's to carry, never this
 * module's to round away. A non-finite value pins to the start rather than propagating a `NaN`
 * into every readout on the screen.
 */
function clampTick(tick: number, trace: ReviewTrace): number {
  if (!Number.isFinite(tick)) {
    return trace.firstTick;
  }
  return Math.min(trace.terminalTick, Math.max(trace.firstTick, Math.floor(tick)));
}

function documentFrom(result: HandEditResult): DocumentSnapshot {
  return {
    version: result.version,
    source: result.document,
    // Carried through as-is, `undefined` included: `result.documentValid` is already the tri-state
    // the practice route's own omission requires, and `DocumentSnapshot.valid`'s readers already
    // resolve that absence themselves (`?? true`) rather than this constructor guessing early.
    valid: result.documentValid,
    // `documentDiagnostics` is optional on `HandEditResult` for the same reason, but this field
    // stays a plain array — an absent diagnostics list reads the same as an empty one to every
    // consumer, so there is no third state worth threading through here.
    diagnostics: result.documentDiagnostics === undefined ? [] : [...result.documentDiagnostics],
  };
}

/**
 * `(state, event) → { state, effects }`.
 *
 * Total and pure: it reads nothing but its arguments, reaches no clock, no store and no network,
 * and every branch either produces a new state or explicitly discards.
 */
export function sandboxReducer(state: SandboxState, event: SandboxEvent): SandboxTransition {
  // Unmount first, before anything else can look at the event: after cancellation there is no
  // state worth moving and no effect worth issuing.
  if (state.cancelled) {
    return event.kind === 'cancelled' ? settle(state) : discard(state);
  }

  switch (event.kind) {
    case 'cancelled':
      return settle({ ...state, cancelled: true });

    // --- player intents ---------------------------------------------------

    case 'select-version': {
      if (state.turnInFlight) {
        return settle({ ...state, notice: error(TURN_WITHHELD_MESSAGE) });
      }
      const epoch = state.epoch + 1;
      const selected: SandboxState = {
        ...state,
        epoch,
        notice: null,
        selection: { kind: 'pending', epoch, ordinal: event.ordinal, runId: event.runId },
        // The stale frame goes NOW, not when the new one arrives: for the moments in between,
        // an old run's world beside a new version's row is exactly the crossed pairing this
        // module exists to make unreachable.
        world:
          event.runId === null
            ? { kind: 'empty', reason: 'not-run' }
            : // No prefix is carried: whatever was on screen belonged to the version being replaced,
              // and holding it under the new one is exactly the crossed pairing this module exists to
              // make unreachable.
              { kind: 'loading', runId: event.runId, watching: null },
        playback: { playheadTick: null, playing: false },
        // **I2.** A save or submit in flight was made against the document this selection is
        // replacing, so it is superseded here rather than left to answer against an epoch that
        // has moved — which is what used to leave the transport reading busy forever. Its
        // response is discarded when it arrives; an *accepted* submission is the one thing that
        // survives, adopted by `submit-settled` so the run stays tracked.
        work: { kind: 'idle' },
      };
      // **I1.** The head is a mutation, so one activation at a time. The display has already
      // moved (the epoch is bumped either way); only the request waits its turn.
      if (state.activating) {
        return settle({ ...selected, queuedActivation: event.ordinal });
      }
      return settle({ ...selected, activating: true, queuedActivation: null }, [
        { kind: 'activate-version', epoch, ordinal: event.ordinal },
      ]);
    }

    case 'document-edited': {
      // Typing the document back to what the server holds is not an edit — the draft disappears
      // rather than leaving a dirty flag nothing can clear.
      const clean = state.document !== null && state.document.source === event.source;
      return settle({
        ...state,
        draft: clean
          ? null
          : {
              source: event.source,
              // Omitted rather than defaulted: `compiles: undefined` and "no verdict yet" must be the
              // same state, or a pending device compile would read as a failure.
              ...(event.compiles === undefined ? {} : { compiles: event.compiles }),
              diagnostics: [...event.diagnostics],
            },
      });
    }

    case 'draft-compiled': {
      // Only for the draft in hand. A verdict for bytes the player has already typed past is stale and
      // is dropped — attaching it would label the current draft with an older draft's errors.
      if (state.draft === null || state.draft.source !== event.source) {
        return settle(state);
      }
      return settle({
        ...state,
        draft: {
          source: state.draft.source,
          compiles: event.compiles,
          diagnostics: [...event.diagnostics],
        },
      });
    }

    case 'hand-edit-requested': {
      if (state.turnInFlight) {
        return settle({ ...state, notice: error(TURN_WITHHELD_MESSAGE) });
      }
      // Nothing uncommitted, or something already in flight: no second commit, no epoch bump.
      if (state.draft === null || state.work.kind !== 'idle') {
        return settle(state);
      }
      const epoch = state.epoch + 1;
      return settle(
        {
          ...state,
          epoch,
          notice: null,
          work: { kind: 'saving', epoch },
        },
        [
          {
            kind: 'save-document',
            epoch,
            source: state.draft.source,
            baseVersion: state.document?.version ?? null,
          },
        ],
      );
    }

    case 'run-requested': {
      if (state.turnInFlight) {
        return settle({ ...state, notice: error(TURN_WITHHELD_MESSAGE) });
      }
      // **The double-submit gate.** A second activation while the first is still in flight must
      // not send a second request — and must not bump the epoch, or it would discard the first's
      // answer.
      if (state.work.kind !== 'idle' || state.run.kind === 'in-flight') {
        return settle(state);
      }
      // `=== false`, never a falsy check: an **unknown** verdict is not a failure, and blocking on one
      // would fabricate the zero-error message below for a draft nothing has judged yet.
      if (state.draft?.compiles === false) {
        return settle({
          ...state,
          notice: error(documentBlockedMessage(state.draft.diagnostics.length)),
        });
      }
      // Design `rounds-candidate-document` **D8**: Run is **one request**, carrying the working
      // document and its base ordinal — never a document write followed by a separate submission.
      // The server commits an uncommitted document as part of this same request (or cuts nothing,
      // when the bytes did not move); the client no longer decides which by branching on `draft`.
      const epoch = state.epoch + 1;
      return settle({ ...state, epoch, notice: null, work: { kind: 'submitting', epoch } }, [
        { kind: 'submit-run', epoch, candidate: event.candidate },
      ]);
    }

    case 'poll-requested': {
      if (state.run.kind !== 'in-flight') {
        return settle(state);
      }
      const pollSeq = state.run.issuedPollSeq + 1;
      return settle({ ...state, run: { ...state.run, issuedPollSeq: pollSeq } }, [
        { kind: 'poll-run', runId: state.run.runId, pollSeq },
      ]);
    }

    // --- the playhead -----------------------------------------------------
    //
    // Three arms, no effects, no clock. They exist because before them `playback` could only be
    // written by a trace load: the spec's "a published run is seekable across its whole range" and
    // "a completed run begins playing" (i.e. *advances*) were both unreachable from a gesture, and
    // the transport's `playing`/`paused` states had no player-driven route into them.

    case 'seek': {
      // **The seek rule is `written-extent.ts`'s, composed rather than restated.** Asking the same
      // function the track's own `aria-disabled` asks is what stops a track that looks draggable
      // from refusing every drag, or worse, the reverse.
      //
      // A *server*-executed run publishes atomically (ROUNDS-MODE §5.3), so while one is in flight
      // there is no readable prefix and its track is a progress bar and nothing more (architecture
      // review **A1**, **D-182**). A *device*-executed run's writer is this client, so a target
      // inside its written extent resolves and one beyond it is reported absent — the split is
      // `state.runExecution`'s, decided once at construction.
      const extent = writtenExtentFor(state);
      const decision = decideSeekToTick(extent, event.tick);
      if (decision.kind === 'refused') {
        // Deliberately silent and deliberately not counted: this is a gesture the extent does not
        // admit, not a stale result that arrived. `SEEK_REFUSAL_MESSAGES` is the track's to say.
        return settle(state);
      }
      return settle({
        ...state,
        // Repositions playback; it does not start or stop it. A player who scrubs while watching
        // is still watching.
        //
        // The second clamp is the loaded trace's own bounds, which a published extent's window
        // already equals — it is kept because a trace whose `firstTick` is not 0 must not be
        // seekable below it, and dropped for an in-flight extent, whose bound is the write head
        // rather than any loaded trace.
        playback: {
          ...state.playback,
          playheadTick:
            extent.kind === 'published' && state.world.kind === 'loaded'
              ? clampTick(decision.tick, state.world.trace)
              : decision.tick,
        },
      });
    }

    case 'playback-toggled': {
      const trace = watchableTrace(state);
      const upper = watchableUpperTick(state);
      if (trace === null || upper === null) {
        return settle(state);
      }
      if (state.playback.playing) {
        return settle({ ...state, playback: { ...state.playback, playing: false } });
      }
      const at = clampTick(state.playback.playheadTick ?? trace.firstTick, trace);
      return settle({
        ...state,
        playback: {
          // Play at the end **restarts**. The bar carries no stop control (**D4**) — returning to the
          // start is the thumb's job — so the alternative is a control that visibly does nothing,
          // which is the one outcome a player reads as broken. The "end" is the *upper* tick, not the
          // trace's terminal one: on a watched run those differ, and restarting a run whose playhead
          // has merely caught the write head would throw away what the player was watching.
          playheadTick: at >= upper ? trace.firstTick : at,
          playing: true,
        },
      });
    }

    case 'playhead-advanced': {
      const trace = watchableTrace(state);
      const upper = watchableUpperTick(state);
      if (!state.playback.playing || trace === null || upper === null) {
        return settle(state);
      }
      const whole = Math.floor(event.ticks);
      if (!Number.isFinite(whole) || whole < 1) {
        // Less than a tick's worth of frame. The loop keeps the remainder and comes back — this
        // module rounding it away every frame is how a 1× playhead ends up never moving at all.
        return settle(state);
      }
      const next = Math.min(
        upper,
        clampTick(state.playback.playheadTick ?? trace.firstTick, trace) + whole,
      );
      // A published run **stops** at its end: the transport falls back to `paused`, and pressing
      // it again restarts from the first tick. A watched run does **not** — catching the write
      // head is not the end of anything, and there are more ticks coming, so the playhead holds
      // the last written moment and resumes advancing when the writer moves on.
      const playing = state.world.kind === 'in-flight' || next < upper;
      // **A pinned playhead returns the state it was given, by identity.**
      //
      // Once the playhead reaches the watched head it stays there until more of the run crosses, and
      // the loop keeps offering whole ticks at the playback rate throughout — fifteen a second at 1×.
      // Each one used to produce a fresh state object and therefore notify every subscriber, which is
      // a whole-screen re-render for a playhead that did not move: ~300 of them across a 20 s tail.
      // Returning the same reference makes `dispatch` skip the notification (see `subscribe`), which
      // is the only thing those renders could have been for.
      //
      // Deliberately narrow: identity is returned only when BOTH fields are unchanged, so this cannot
      // swallow an update any consumer could observe.
      if (next === state.playback.playheadTick && playing === state.playback.playing) {
        return settle(state);
      }
      return settle({
        ...state,
        playback: { playheadTick: next, playing },
      });
    }

    // --- results ----------------------------------------------------------

    case 'activation-settled': {
      // Whatever this answer says, the activation it belongs to is no longer outstanding — so the
      // gate opens here, before any epoch check, or a discarded response would wedge the queue
      // shut and the player's last selection would never reach the server (**I1**).
      const released: SandboxState = { ...state, activating: false };
      if (state.queuedActivation !== null) {
        // A queued intent can only exist because the player selected again after this activation
        // went out, which means this answer is stale by construction. Discard it and send the
        // intent it was superseded by — the server sees the two in the order they were asked for.
        return settle(
          {
            ...released,
            discarded: state.discarded + 1,
            activating: true,
            queuedActivation: null,
          },
          [{ kind: 'activate-version', epoch: state.epoch, ordinal: state.queuedActivation }],
        );
      }
      if (event.epoch !== state.epoch) {
        return discard(released);
      }
      if (event.dispatch.kind !== 'applied') {
        const detail =
          event.dispatch.kind === 'failed'
            ? (event.dispatch.detail ?? 'the request did not answer')
            : event.dispatch.kind === 'turnInFlight'
              ? 'a Beekeeper turn holds this colony'
              : 'that version is not in this sandbox';
        return settle({
          ...released,
          // The transaction failed whole: the document never moved, so neither the old world nor a
          // new one may be shown as belonging to it.
          selection: { kind: 'none' },
          world: { kind: 'empty', reason: 'load-failed' },
          notice: error(activationFailedMessage(event.ordinal, detail)),
        });
      }
      const document = documentFrom(event.dispatch.result);
      const runId = state.selection.kind === 'none' ? null : state.selection.runId;
      const activated: SandboxState = {
        ...released,
        document,
        // Selecting a version *is* the request to replace the working document (spec: "activate
        // that version as the working document"), so an uncommitted draft does not survive it.
        draft: null,
      };
      if (runId === null) {
        return settle({
          ...activated,
          selection: { kind: 'settled', epoch: state.epoch, ordinal: event.ordinal, runId: null },
          world: { kind: 'empty', reason: 'not-run' },
        });
      }
      return settle(activated, [
        { kind: 'load-trace', epoch: state.epoch, runId, reason: 'selection' },
      ]);
    }

    case 'trace-settled': {
      if (event.epoch !== state.epoch) {
        return discard(state);
      }
      const selection: SandboxSelection =
        state.selection.kind === 'pending'
          ? { ...state.selection, kind: 'settled' }
          : state.selection;
      if (event.outcome.kind !== 'loaded') {
        // The world is empty either way — there is nothing to draw — but *why* is a different fact
        // in each case, and the notice is the only place the player learns which (task 4.6).
        const notice =
          event.outcome.kind === 'unsupported'
            ? info(traceStaleFormatMessage(selectedOrdinal(selection)))
            : error(
                traceLoadFailedMessage(
                  selectedOrdinal(selection),
                  traceFailureDetail(event.outcome),
                ),
              );
        return settle({
          ...state,
          selection,
          world: { kind: 'empty', reason: 'load-failed' },
          playback: { playheadTick: null, playing: false },
          notice,
        });
      }
      const trace = event.outcome.trace;
      // **Was the playhead already inside this very run?** Only the `loading` arm can answer that,
      // and only because it carried the watched prefix here (see `SandboxWorld`): the run has already
      // settled by now, so `state.run` no longer distinguishes "the run we were watching" from any
      // other completed one.
      const superseding =
        state.world.kind === 'loading' &&
        state.world.runId === event.runId &&
        state.world.watching !== null;
      return settle({
        ...state,
        selection,
        world: { kind: 'loaded', runId: event.runId, trace },
        // **A better reading of the moment already on screen keeps that moment.** Publishing a run
        // the player was watching is not an arrival, it is the same run re-read verified, so the
        // playhead stays where they left it (clamped, because a run that terminated early publishes a
        // shorter trace than the horizon its watched views reported) and `playing` is theirs — a
        // player who paused to look at something stays paused.
        //
        // Otherwise the trace is genuinely new to the screen and opens at its own first tick: a
        // completed run nobody was watching starts playing (`client-sandbox-screen`/a-completed-run-
        // begins-playing-without-navigating), a selected one waits for the player.
        playback: superseding
          ? {
              playheadTick: clampTick(state.playback.playheadTick ?? trace.firstTick, trace),
              playing: state.playback.playing,
            }
          : { playheadTick: trace.firstTick, playing: event.reason === 'completion' },
        notice: null,
      });
    }

    case 'save-settled': {
      if (event.epoch !== state.epoch || state.work.kind !== 'saving') {
        return discard(state);
      }
      if (event.dispatch.kind === 'refused') {
        return settle({
          ...state,
          work: { kind: 'idle' },
          notice: error(saveRefusalMessage(event.dispatch.reason)),
        });
      }
      // The whole-document hand edit's own settlement. Run no longer routes through here (**D8**
      // above) — this is `hand-edit-requested`'s save, and it stops at `idle`.
      const document = documentFrom(event.dispatch.result);
      // **A newer edit may have landed in `draft` while this save was in flight** (adversarial
      // review F1). `hand-edit-requested` already refuses to issue a second `save-document` while
      // `work` is not `idle`, but that only stops a second *request* — a second `document-edited`
      // still lands, and the bytes it wrote have no save of their own behind them yet. Clearing
      // `draft` unconditionally here took that edit down with the settlement of a request it was
      // never part of. So it is cleared only when it still names exactly the bytes this response
      // committed; anything else is a newer, not-yet-saved edit and survives.
      const draftStillMatchesCommit = state.draft !== null && state.draft.source === document.source;
      return settle({
        ...state,
        document,
        draft: draftStillMatchesCommit ? null : state.draft,
        work: { kind: 'idle' },
        selection: {
          kind: 'settled',
          epoch: state.epoch,
          ordinal: document.version,
          runId: null,
        },
      });
    }

    case 'submit-settled': {
      if (event.epoch !== state.epoch || state.work.kind !== 'submitting') {
        // **The one result that may not simply be dropped (I2).** A selection (or a turn) moved
        // the epoch while this POST was in the air; if the server *accepted* it, a run is now
        // being written and nobody is awaiting it — its score would never land and the transport
        // would never learn it exists. So an accepted submission is adopted here on its own terms:
        // stamped with the **submit's** epoch, which is by definition no longer the current one,
        // so autoplay stays suppressed and the completion is stated rather than yanking the world
        // the player chose instead. A refusal has nothing to adopt and is discarded as before.
        if (event.submission.kind !== 'accepted' || state.run.kind === 'in-flight') {
          return discard(state);
        }
        // Deliberately **not** counted in `discarded`: this answer was not dropped, it was
        // adopted on narrower terms. Only the parts that belonged to the superseded intent —
        // the `work` transition and its notice — fall away.
        return settle(
          {
            ...state,
            run: {
              kind: 'in-flight',
              runId: event.submission.runId,
              epoch: event.epoch,
              ticksObserved: 0,
              expectedTerminalTick: null,
              issuedPollSeq: 0,
              appliedPollSeq: 0,
            },
          },
          [{ kind: 'await-completion', runId: event.submission.runId }],
        );
      }
      if (event.submission.kind === 'refused') {
        const refusal = event.submission.refusal;
        return settle({
          ...state,
          // Idle, not stuck pending: the control offers Run again and the editor is untouched.
          work: { kind: 'idle' },
          notice: error(
            runRefusalMessage(refusal),
            refusal.kind === 'compileFailed' ? refusal.diagnostics : [],
          ),
        });
      }
      // A dirty press: the draft's bytes are now what the server has, whether or not they moved
      // to a new ordinal (design D8: the commit is folded into this same request). The draft
      // therefore stops being "uncommitted" — clearing it without folding its text into `document`
      // would snap the editor back to the pre-edit bytes the instant the draft disappears, which
      // is worse than what folding fixes.
      //
      // The ordinal is applied **directly and finally** from `event.submission.documentVersion` —
      // this run's own committed version, as the accept response now names it (module note
      // above). There is no follow-up read to fetch it and nothing left provisional: reading the
      // sandbox's mutable head after the fact is exactly the lost-update race this response field
      // exists to close (implementation-review HIGH) — a second writer advancing the head between
      // this commit and a later read would have attributed *their* ordinal to this run.
      const wasDirty = state.draft !== null;
      return settle(
        {
          ...state,
          document: wasDirty
            ? {
                version: event.submission.documentVersion,
                source: state.draft!.source,
                valid: true,
                diagnostics: [],
              }
            : state.document,
          draft: null,
          work: { kind: 'idle' },
          notice: null,
          run: {
            kind: 'in-flight',
            runId: event.submission.runId,
            epoch: state.epoch,
            ticksObserved: 0,
            expectedTerminalTick: null,
            issuedPollSeq: 0,
            appliedPollSeq: 0,
          },
        },
        // One awaited completion per run: `awaitCompletion` is a one-shot long-lived GET, so it is
        // issued here and never re-issued. Nothing else is issued alongside it any more — the
        // ordinal above needed no read to correct it.
        [{ kind: 'await-completion', runId: event.submission.runId }],
      );
    }

    case 'run-progress': {
      if (state.run.kind !== 'in-flight' || state.run.runId !== event.runId) {
        return discard(state);
      }
      // **The stale-poll gate.** Ordering is the poll's own stamp, not the tick count: two reads
      // can legitimately report the same extent, and only the sequence says which is newer.
      if (event.pollSeq <= state.run.appliedPollSeq) {
        return discard(state);
      }
      if (event.dto === null) {
        // The read failed. The sequence still advances — the answer *arrived* — but nothing is
        // invented from it.
        return settle({ ...state, run: { ...state.run, appliedPollSeq: event.pollSeq } });
      }
      return settle({
        ...state,
        run: {
          ...state.run,
          appliedPollSeq: event.pollSeq,
          // A written extent only ever grows; the server cannot un-write ticks.
          ticksObserved: Math.max(state.run.ticksObserved, event.dto.ticksObserved),
          expectedTerminalTick: event.dto.terminalTick ?? state.run.expectedTerminalTick,
          // Terminality is `awaitCompletion`'s to declare, not a poll's: one signal, one owner.
        },
      });
    }

    case 'run-watched': {
      // **The execution-site gate, and it is the load-bearing line in this arm.** A watched extent is
      // legitimate only where this client owns the writer. For a server-executed run the same bytes
      // sit across a process boundary and reading them early would bypass the commit marker, the
      // per-component checksums and the index bounds validation — the plausible-but-false-replay class
      // **D-121** guards. `runExecution` is set once at construction and defaults to `'server'`, so a
      // coordinator that was never told lands on the refusing side, exactly as `written-extent.ts`'s
      // required `site` does. Counted as discarded, because unlike a refused seek this is a *result*
      // that arrived and was dropped, and a test should be able to prove it arrived.
      if (state.runExecution !== 'device') {
        return discard(state);
      }
      if (state.run.kind !== 'in-flight' || state.run.runId !== event.runId) {
        return discard(state);
      }
      if (state.run.epoch !== state.epoch) {
        // The player has moved to another version since pressing Run. `run-completed` leaves the world
        // alone in exactly this case and states the completion instead; a watched view has nothing to
        // state, so it is simply dropped. Yanking the world they chose would be worse here than
        // there — this happens dozens of times a run.
        return discard(state);
      }
      const first = state.world.kind !== 'in-flight' || state.world.runId !== event.runId;
      return settle({
        ...state,
        world: {
          kind: 'in-flight',
          runId: event.runId,
          verification: 'in-process-unverified',
          watching: event.watching,
        },
        // **Watching begins at the first view and not before.** The playhead is placed on the run's
        // own first tick and playback starts, so the player watches from the first moments rather than
        // waiting for completion (`client-practice`) — and only on the *first* view for this run, so
        // every later delta leaves a playhead the player may since have dragged exactly where it is.
        playback: first
          ? { playheadTick: event.watching.firstTick, playing: true }
          : state.playback,
        // A watched view is not an announcement. Any notice standing — a refusal, a completion
        // elsewhere — is the player's to read, and thirty of these a run must not clear it.
      });
    }

    case 'run-completed': {
      if (state.run.kind !== 'in-flight' || state.run.runId !== event.outcome.runId) {
        return discard(state);
      }
      const runEpoch = state.run.epoch;
      const settledRun: SandboxRun = {
        kind: 'settled',
        runId: event.outcome.runId,
        outcome: event.outcome,
      };
      if (event.outcome.kind === 'failed') {
        return settle({
          ...state,
          run: settledRun,
          work: { kind: 'idle' },
          notice: error(
            runFailureMessage(event.outcome.runId, event.outcome.status, event.outcome.detail),
          ),
        });
      }
      // Autoplay only if the player is still on the version they ran. Otherwise the completion is
      // recorded and stated, and the world they are watching is left alone.
      if (runEpoch !== state.epoch) {
        return settle({
          ...state,
          run: settledRun,
          notice: info(runCompletedElsewhereMessage(event.outcome.runId, event.outcome.score)),
        });
      }
      return settle(
        {
          ...state,
          run: settledRun,
          notice: null,
          world: {
            kind: 'loading',
            runId: event.outcome.runId,
            // **The one place the watched prefix survives its own run's completion.** Carried only
            // for the run that just landed: a prefix for some *other* run would be a stale world
            // held under a new one. Everything else — a server-executed run, a run nobody watched —
            // carries `null` and loads with an empty world exactly as before.
            watching:
              state.world.kind === 'in-flight' && state.world.runId === event.outcome.runId
                ? state.world.watching
                : null,
          },
        },
        [
          {
            kind: 'load-trace',
            epoch: state.epoch,
            runId: event.outcome.runId,
            reason: 'completion',
          },
        ],
      );
    }

    // --- the Beekeeper's turn ---------------------------------------------

    case 'turn-started':
      return settle({ ...state, turnInFlight: true });

    case 'turn-settled': {
      const cleared: SandboxState = { ...state, turnInFlight: false };
      if (event.document === null) {
        return settle(cleared);
      }
      // The turn replaced the whole document, so it relocates the epoch — which is what discards a
      // hand edit still in flight (architecture review **A3**). The player's text is kept: the
      // edit did not land, but nothing typed is thrown away.
      const superseded = state.work.kind !== 'idle';
      const epoch = state.epoch + 1;
      const landed: SandboxState = {
        ...cleared,
        epoch,
        document: event.document,
        work: { kind: 'idle' },
        // A selection still waiting its turn is dropped rather than issued after the turn's
        // document has landed: sending it would move the head away from the version the selector
        // is now showing, which is the very disagreement **I1** closes. The activation already on
        // the wire is not recalled — its answer arrives stale and is discarded, and the turn's
        // own version is the head the player is looking at.
        queuedActivation: null,
        selection: {
          kind: 'settled',
          epoch,
          ordinal: event.document.version,
          runId: null,
        },
        notice: superseded ? error(HAND_EDIT_SUPERSEDED_MESSAGE) : state.notice,
      };

      /**
       * **The version the turn cut runs itself** (WAG-96).
       *
       * Everything above settles the *document*: the agent's bytes are the working ones and the
       * selector marks its ordinal active. Nothing above settles the *world* — it goes on holding
       * whichever trace was under the playhead before the turn, so the screen named one version
       * and played another, and the only route to seeing the new script move was to reopen the
       * selector, reselect the version already marked active, and press Run. That is the gesture
       * this removes: a turn that cuts a version is a selection *and* a Run, said once.
       *
       * It is issued from here rather than from the screen for the reason every other transaction
       * is: the run must carry the epoch the turn just relocated to, or `run-settled`'s autoplay
       * check (`run.epoch === state.epoch`) refuses to show the very trace this started. The
       * candidate is the turn's own document — a `RunCandidate` is exactly `{document,
       * baseOrdinal}` (design **D8**), both of which arrived on the event.
       *
       * Three states decline it, and each is the same rule `run-requested` already enforces:
       *
       *   * a document the compiler has **already refused** (`valid === false`) — the server would
       *     refuse the submission and the diagnostics are on screen either way, so the request is
       *     not worth making;
       *   * a **run already in flight**, the player's own — a sandbox writes one run at a time, and
       *     yanking theirs for the agent's would discard what they are watching being written;
       *   * work in flight, or an **uncommitted hand edit** the turn superseded. The draft survives
       *     the turn deliberately (the text is not thrown away), and while it does it is what the
       *     editor shows and what the selector marks active — so a run of the agent's bytes would
       *     be a run of a document nobody is looking at. The player presses Run when they have
       *     decided which of the two they want.
       *
       * A fourth reason declines it, a duller one: a document reporting ordinal `0` — the "no known
       * base" sentinel a surface that names no version lands on — has no base to declare, and the
       * server's base-ordinal CAS would refuse the submission as stale. The player's own Run press
       * has a fallback this does not (the fetched head, `RoundsController`'s `headVersion ??
       * versionHistory.headOrdinal`), so the honest move here is to leave the press to them rather
       * than to guess an ordinal. Both shipped server branches send the ordinal, so this is the
       * unreachable arm on a current deployment rather than the common case.
       */
      const autoRunnable =
        event.document.valid !== false &&
        event.document.version > 0 &&
        state.run.kind !== 'in-flight' &&
        state.work.kind === 'idle' &&
        state.draft === null;
      if (!autoRunnable) {
        return settle(landed);
      }
      return settle({ ...landed, notice: null, work: { kind: 'submitting', epoch } }, [
        {
          kind: 'submit-run',
          epoch,
          candidate: { document: event.document.source, baseOrdinal: event.document.version },
        },
      ]);
    }

    case 'preview-requested': {
      // Idempotent: already trying, or something else already claims the world (a selection,
      // a run, or an earlier preview load that already landed) — either way there is nothing
      // new to ask for.
      if (
        state.previewRequested ||
        state.world.kind !== 'empty' ||
        state.selection.kind !== 'none' ||
        state.run.kind !== 'none'
      ) {
        return settle(state);
      }
      return settle({ ...state, previewRequested: true }, [{ kind: 'open-preview' }]);
    }

    case 'preview-settled': {
      const cleared: SandboxState = { ...state, previewRequested: false };
      if (event.outcome.kind !== 'loaded') {
        // Pending, failed, corrupt or unreachable: rendered as nothing. The next
        // `preview-requested` — the screen's own retry cadence — tries again.
        return settle(cleared);
      }
      // Nothing else may already claim the world slot: a selection, a run, or an earlier
      // preview load all win outright. This is what makes "the player's own first run replaces
      // the preview and the preview does not return" true by construction rather than by a
      // screen remembering to stop asking — the coordinator refuses the write, not just the
      // caller.
      if (
        cleared.world.kind !== 'empty' ||
        cleared.selection.kind !== 'none' ||
        cleared.run.kind !== 'none'
      ) {
        return discard(cleared);
      }
      return settle({
        ...cleared,
        world: { kind: 'loaded', runId: PREVIEW_RUN_ID, trace: event.outcome.trace, isPreview: true },
        // **It plays as it lands.** The requirement is "a hive that is already working rather than
        // an empty field with a button" — a still first frame is neither, and the transport is no
        // longer the thing that would start it (it offers Run, since the preview is not the
        // player's run). Nothing else in the reducer starts playback for a trace nobody selected.
        playback: { playheadTick: event.outcome.trace.firstTick, playing: true },
      });
    }
  }
}

// ---------------------------------------------------------------------------
// The driver
// ---------------------------------------------------------------------------

/** The stateful handle the screen holds. */
export interface SandboxCoordinator {
  getState(): SandboxState;
  dispatch(event: SandboxEvent): void;
  /**
   * Notified after every **state change**. Returns an unsubscribe.
   *
   * Not after every *dispatch*, which is what this said until the watch-window work: an event the
   * reducer answers with the state it was given — a playhead pinned at the watched head is the case
   * that motivated it — notifies nobody, because there is nothing to redraw. Subscribers are
   * render triggers, and a listener that needs to observe *dispatches* rather than *changes* wants a
   * different seam (`effectLog` records what was issued regardless).
   *
   * The guarantee is unchanged where it matters: every state a consumer could distinguish from the
   * last one is still delivered exactly once.
   */
  subscribe(listener: (state: SandboxState) => void): () => void;
  /** Every effect issued, oldest first — the ordering record the "Run sends the document in a
   * single request" assertion reads rather than inferring the sequence from the transport. */
  readonly effectLog: readonly SandboxEffect[];
}

function describeError(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * Wraps the reducer with the port, so the screen dispatches events and never awaits anything.
 *
 * The driver is deliberately decision-free: it performs the effect it is handed, turns whatever
 * comes back — including a rejected promise — into a typed event, and dispatches it. **Every rule
 * about what to do with a result lives in the reducer**, including the rule that a result arriving
 * after unmount is dropped. That is why the driver does not bother cancelling in-flight promises:
 * a cancelled reducer discards their answers, which is the same outcome with none of the
 * abort-plumbing.
 */
export function createSandboxCoordinator(
  port: SandboxPort,
  initial: SandboxState = initialSandboxState(),
): SandboxCoordinator {
  let state = initial;
  const effectLog: SandboxEffect[] = [];
  const listeners = new Set<(next: SandboxState) => void>();

  function dispatch(event: SandboxEvent): void {
    const transition = sandboxReducer(state, event);
    // **Whether the state actually moved**, taken before the assignment below. The reducer returns
    // the state it was given, by identity, for a transition with nothing to show — a playhead pinned
    // at the watched head being the case that motivated it. Notifying subscribers for one of those is
    // a whole-screen re-render that redraws exactly what is already on screen.
    const changed = transition.state !== state;
    state = transition.state;
    for (const effect of transition.effects) {
      effectLog.push(effect);
    }
    // Effects are still logged and performed for an unchanged state: "nothing to redraw" and "nothing
    // to do" are different claims, and only the first is being made here.
    if (changed) {
      for (const listener of listeners) {
        listener(state);
      }
    }
    for (const effect of transition.effects) {
      perform(effect);
    }
  }

  function perform(effect: SandboxEffect): void {
    switch (effect.kind) {
      case 'activate-version':
        void port.activateVersion(effect.ordinal).then(
          (result) =>
            dispatch({
              kind: 'activation-settled',
              epoch: effect.epoch,
              ordinal: effect.ordinal,
              dispatch: result,
            }),
          (cause: unknown) =>
            dispatch({
              kind: 'activation-settled',
              epoch: effect.epoch,
              ordinal: effect.ordinal,
              dispatch: { kind: 'failed', detail: describeError(cause) },
            }),
        );
        return;

      case 'load-trace':
        void port.openTrace(effect.runId).then(
          (load) =>
            dispatch({
              kind: 'trace-settled',
              epoch: effect.epoch,
              runId: effect.runId,
              reason: effect.reason,
              outcome: load,
            }),
          (cause: unknown) =>
            dispatch({
              kind: 'trace-settled',
              epoch: effect.epoch,
              runId: effect.runId,
              reason: effect.reason,
              outcome: { kind: 'unavailable', message: describeError(cause) },
            }),
        );
        return;

      case 'save-document':
        void port.saveDocument(effect.source, effect.baseVersion).then(
          (result) => dispatch({ kind: 'save-settled', epoch: effect.epoch, dispatch: result }),
          () =>
            dispatch({
              kind: 'save-settled',
              epoch: effect.epoch,
              // No typed reason came back, so none is invented: `invalid` is the surface's own
              // catch-all, and the sentence the player sees says the save was refused.
              dispatch: { kind: 'refused', reason: { kind: 'invalid' } },
            }),
        );
        return;

      case 'submit-run':
        void port.submitRun(effect.candidate).then(
          (submission) => dispatch({ kind: 'submit-settled', epoch: effect.epoch, submission }),
          (cause: unknown) =>
            dispatch({
              kind: 'submit-settled',
              epoch: effect.epoch,
              submission: {
                kind: 'refused',
                refusal: { kind: 'unavailable', message: describeError(cause) },
              },
            }),
        );
        return;

      case 'await-completion':
        void port.awaitCompletion(effect.runId).then(
          (outcome) => dispatch({ kind: 'run-completed', outcome }),
          (cause: unknown) =>
            dispatch({
              kind: 'run-completed',
              outcome: {
                kind: 'failed',
                runId: effect.runId,
                status: 'unavailable',
                detail: describeError(cause),
              },
            }),
        );
        return;

      case 'poll-run':
        void port.fetchRun(effect.runId).then(
          (dto) =>
            dispatch({ kind: 'run-progress', runId: effect.runId, pollSeq: effect.pollSeq, dto }),
          // A failed poll is not player-facing: the extent simply does not advance, and the next
          // poll retries. The sequence still advances so a late success cannot overwrite a newer one.
          () =>
            dispatch({
              kind: 'run-progress',
              runId: effect.runId,
              pollSeq: effect.pollSeq,
              dto: null,
            }),
        );
        return;

      case 'open-preview':
        void port.openPreview().then(
          (outcome) => dispatch({ kind: 'preview-settled', outcome }),
          (cause: unknown) =>
            dispatch({
              kind: 'preview-settled',
              outcome: { kind: 'unavailable', message: describeError(cause) },
            }),
        );
        return;
    }
  }

  return {
    getState: () => state,
    dispatch,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    effectLog,
  };
}
